import React, { useEffect, useRef, useState } from 'react';
import { ViewProduct } from '../models/ViewProduct.model';
import { SelectionType } from '../enums/SelectionType.enum';
import { PageSubContext } from '../enums/PageSubContext.enum';
import { ProductHandle } from '../enums/ProductHandle.enum';
import { useRecoilValue } from 'recoil';
import { pageSubContextState, shopifyState, abTestState, environmentContextState } from '../recoil/atoms';
import { HomeLander } from './HomeLander/HomeLander';
import { SuperPowerShortLander } from './SuperPowerShortLander/SuperPowerShortLander';
import { useProductSelection } from '../hooks/useProductSelection';
import { CrossOverBraLander } from './CrossOverBraLander/CrossOverBraLander';
import { SuperPowerThongLander } from './SuperPowerThongLander/SuperPowerThongLander';
import { QueenBriefLander } from './QueenBriefLander/QueenBriefLander';
import { VNeckBraLander } from './VNeckBraLander/VNeckBraLander';
import { LeggingTwoLander } from './LeggingTwoLander/LeggingTwoLander';
import { CardFooter } from '../components/CardFooter';
import { CardHeader } from '../components/CardHeader';
import { NavCategory } from '../enums/NavCategory.enum';
import { NavCategoryContext } from '../enums/NavCategoryContext.enum';
import { useNavCategories } from '../hooks/actions/useNavCategories';
import { isMobileState } from '../recoil/selectors';
import { ProductDetailPageDesktop } from './ProductDetailLander/ProductDetailPageDesktop';
import { ProductDetailPageMobile } from './ProductDetailLander/ProductDetailPageMobile';
import { BridalLander } from './BridalLander/BridalLander';
import { LiftWearTankLander } from './LiftWearTankLander/LiftWearTankLander';
import { LiftWearCamiLander } from './LiftWearCamiLander/LiftWearCamiLander';
import { SilhouetteBraLander } from './SilhouetteBraLander/SilhouetteBraLander';
import { BoldnessBodysuitLander } from './BoldnessBodysuitLander/BoldnessBodysuitLander';
import { SilhouetteBriefLander } from './SilhouetteBriefLander/SilhouetteBriefLander';
import { SuperPowerBriefLander } from './SuperPowerBriefLander/SuperPowerBriefLander';
import { LiftWearTankBodysuitLander } from './LiftWearTankBodysuitLander/LiftWearTankBodysuitLander';
import { BoldnessTankLander } from './BoldnessTankLander/BoldnessTankLander';
import { CamiBodysuitLander } from './CamiBodysuitLander/CamiBodysuitLander';
import { useOnScreen } from '../hooks/useOnScreen';
import { useShowAddToBag } from '../hooks/useShowAddToBag';
import { trackProductDetailViewed } from '../tracking/events';
import { SustainabilityLander } from './SustainabilityLander/SustainabilityLander';
import { EverReadyPantLander } from './EverReadyPantLander/EverReadyPantLander';
import { ShopNowFloatingButton } from '../root/components/ShopNowFloatingButton';
import { LanderContextControlsProvider } from './LanderContext';
import { WaistHeroCincherLander } from './WaistHeroCincher/WaistHeroCincherLander';
import ProductDetailSchema from './ProductDetailLander/ProductDetailSchema';
import CardBody from '../components/CardBody';
import PromoBanner from '../components/PromoBanner';

interface Props {
  mode?: 'PRODUCT';
}

const NONE = (): JSX.Element => <></>;

export const landerMap: { [subContext in PageSubContext | ProductHandle]?: React.FC } = {
  [PageSubContext.Homepage]: HomeLander,
  [PageSubContext.Sustainability]: SustainabilityLander,
  [PageSubContext.Wedding]: BridalLander,
  [ProductHandle.SuperPowerShort]: SuperPowerShortLander,
  [ProductHandle.SuperPowerBrief]: SuperPowerBriefLander,
  [ProductHandle.QueenBrief]: QueenBriefLander,
  [ProductHandle.SuperPowerThong]: SuperPowerThongLander,
  [ProductHandle.BoldnessTank]: BoldnessTankLander,
  [ProductHandle.LiftWearTank]: LiftWearTankLander,
  [ProductHandle.LiftWearCami]: LiftWearCamiLander,
  [ProductHandle.SilhouetteBra]: SilhouetteBraLander,
  [ProductHandle.SilhouetteBrief]: SilhouetteBriefLander,
  [ProductHandle.CrossoverBra]: CrossOverBraLander,
  [ProductHandle.CamiBodysuit]: CamiBodysuitLander,
  [ProductHandle.VNeckBra]: VNeckBraLander,
  [ProductHandle.EverReadyPant]: EverReadyPantLander,
  [ProductHandle.BoldnessBodysuit]: BoldnessBodysuitLander,
  [ProductHandle.LeggingTwo]: LeggingTwoLander,
  [ProductHandle.TankBodysuit]: LiftWearTankBodysuitLander,
  [ProductHandle.EGiftCard]: NONE,
  [ProductHandle.TapeMeasure]: NONE,
  [ProductHandle.IntimatesBag]: NONE,
  [PageSubContext.Other]: NONE,
  [ProductHandle.EverydayCottonBrief]: NONE,
  [ProductHandle.WaistHeroCincher]: WaistHeroCincherLander,
  // discontinued [ProductHandle.SculptwaveCapri]: NONE,
  // discontinued [ProductHandle.EverydayCottonThong]: NONE,
};

export const landerExists = (type: PageSubContext | ProductHandle): boolean => {
  return !!landerMap[type] && landerMap[type] !== NONE;
};

const excludePDPList: (PageSubContext | ProductHandle | undefined)[] = [PageSubContext.Homepage, PageSubContext.Sustainability];

export const LanderApp: React.FC<Props> = ({ mode }) => {
  const { product, color } = useProductSelection(SelectionType.ProductSection, '0');
  // Note this will lock the product for the lander so it doesn't change when toggling the PDP section
  const [landerProduct] = useState<ViewProduct>(product);
  const pageSubContext = useRecoilValue(pageSubContextState);

  const DynamicLander = mode === 'PRODUCT' && landerProduct?.handle ? landerMap[landerProduct.handle] : pageSubContext ? landerMap[pageSubContext] : NONE;

  const isMobile = useRecoilValue(isMobileState);
  const pdpRef = useRef<HTMLDivElement>(null);
  const includePDP = !excludePDPList.includes(pageSubContext) && !excludePDPList.includes(landerProduct.handle);

  // give this a 200px distance to fire this when the use can click the
  // "A color for any mood section" as the product needs to be tracked by that point
  const isPDPOnScreen = useOnScreen(pdpRef, '200px');
  const shopify = useRecoilValue(shopifyState);
  const abTests = useRecoilValue(abTestState);
  const [productViewTracked, setProductViewTracked] = React.useState<boolean>(false);
  const showAddToBag = useShowAddToBag(pdpRef);

  useEffect(() => {
    if (isPDPOnScreen && !productViewTracked) {
      trackProductDetailViewed(shopify, product, undefined, color, undefined);
      setProductViewTracked(true);
    }
  }, [isPDPOnScreen]);

  const ProductPage = isMobile ? ProductDetailPageMobile : ProductDetailPageDesktop;
  useNavCategories({ context: NavCategoryContext.LandingPage, initializeCategory: NavCategory.Bestsellers });
  const environmentContext = useRecoilValue(environmentContextState);

  return (
    <>
      {abTests['AB2022-02'] && includePDP && isMobile && (
        <ShopNowFloatingButton showAddToBag={showAddToBag} scrollToProductSection={() => pdpRef?.current?.scrollIntoView()} />
      )}
      <main role="main">
        <LanderContextControlsProvider scrollToProductSection={() => pdpRef?.current?.scrollIntoView()}>
          {environmentContext.timedSaleActive && mode === 'PRODUCT' && (
            <CardBody>
              <PromoBanner />
            </CardBody>
          )}
          {DynamicLander && <DynamicLander />}
          {includePDP && (
            <>
              <CardHeader />
              <ProductDetailSchema />
              <div ref={pdpRef}>
                <ProductPage />
              </div>
              <CardFooter />
            </>
          )}
        </LanderContextControlsProvider>
      </main>
    </>
  );
};

export default LanderApp;
